import { isEmpty } from "lodash";
import {
  ExtensionURLs,
  getValueTypeByAttribute,
  getAttributeValueOverrideExtensionURL,
  AttributeLabels,
  AttributeDisplayLabels,
  ActionLabels,
  ActionDisplayLabels,
  getIsEmptyAttribute,
  getOverrideExtensionURLByAttribute,
  getAttributeFields,
  BirthDateOverrideURLs,
  GenderOverrideURLs,
  BirthDateRestoreURLs,
  GenderRestoreURLs,
  GenericRestoreURLs,
  MultipleBirthOverrideURLs,
  MultipleBirthRestoreURLs,
} from "./Utils";
import { getOverrideType } from "../RecordOverrideUtil";
import { capitalize } from "../../../../utils/textFunctions";

/**
 * Extracts attribute values from FHIR records and returns them in a structured format.
 *
 * @param {string} attribute - The attribute to extract (e.g., "address", "name", "phone", "email", "identifier",
 *                             "birthDate", "gender").
 * @param {Map} records - A Map of FHIR records to extract the attribute values from.
 * @returns {Array} - An array of Map objects containing the extracted attribute values and related metadata.
 */
const AttributeValues = (attribute, records) => {
  const telecomAttributes = [AttributeLabels.PHONE, AttributeLabels.EMAIL];
  const topLevelExtensionAttributes = [
    AttributeLabels.BIRTHDATE,
    AttributeLabels.GENDER,
  ];

  /**
   * Returns an array of fields to be displayed for a given attribute.
   *
   * @param {string} attribute - The attribute for which to get display fields.
   * @returns {Array} - An array of field names to be displayed.
   */
  const getDisplayFields = (attribute) => {
    switch (attribute) {
      case AttributeLabels.ADDRESS:
        return ["line", "city", "district", "state", "country", "postalCode"];
      case AttributeLabels.NAME:
        return ["family", "given", "suffix"];
      case AttributeLabels.PHONE:
      case AttributeLabels.EMAIL:
      case AttributeLabels.IDENTIFIER:
        return ["value"];
      case AttributeLabels.BIRTHDATE:
        return ["birthDate"];
      case AttributeLabels.GENDER:
        return ["gender"];
      default:
        return [];
    }
  };

  /**
   * Retrieves the previous override values for a given attribute from the object.
   *
   * @param {string} attribute - The attribute to retrieve previous override values for.
   * @param {Object} object - The object from which to retrieve previous override values.
   * @returns {any} - The previous override values for the attribute.
   */
  const getOverridePreviousValues = (attribute, object) => {
    const valueType = getValueTypeByAttribute(attribute);

    switch (attribute) {
      case AttributeLabels.BIRTHDATE:
      case AttributeLabels.GENDER:
        return object?.[valueType]?.toString().toUpperCase();
      default:
        const valueTypeObject = object?.[valueType];
        const extractedValues = extractItemValues(
          attribute,
          valueTypeObject,
          new Map(),
          getAttributeFields(attribute),
        );
        return getDisplayValue(attribute, extractedValues);
    }
  };

  /**
   * Extracts override-related values for a given attribute from the object and adds them to the attribute map.
   *
   * @param {string} attribute - The attribute to extract override values for.
   * @param {Object} object - The object from which to extract override values.
   * @param {Map} attributeMap - A Map to populate with override-related values.
   * @returns {Map} - The updated attribute map with override-related values.
   */
  const extractAttributeOverrideValues = (attribute, object, attributeMap) => {
    const overrideExtensionURL = getOverrideExtensionURLByAttribute(attribute);
    const overrideExtension = object?.extension?.find(
      (e) => e?.url === overrideExtensionURL,
    );

    if (overrideExtension) {
      const fields = getAttributeFields(attribute);
      const overrideType = getOverrideType(attribute, object);
      const valueType = getValueTypeByAttribute(attribute);
      const overriddenPreviousDisplayValue = getOverridePreviousValues(
        attribute,
        overrideExtension,
      );
      attributeMap
        .set("overridePresent", true)
        .set("overridePreviousDisplayValue", overriddenPreviousDisplayValue)
        .set("overrideType", overrideType)
        .set("overrideObject", overrideExtension?.[valueType]);
    }

    return attributeMap;
  };

  /**
   * Extracts the attribute code from the object's extensions and adds it to the attribute map.
   *
   * @param {Object} object - The object from which to extract the attribute code.
   * @param {Map} attributeMap - A Map to populate with the attribute code.
   * @returns {Map} - The updated attribute map with the attribute code.
   */
  const extractAttributeCode = (object, attributeMap) => {
    const attrCodeExtension = object?.extension?.find(
      (e) => e?.url === ExtensionURLs.attrCode,
    );
    const overrideAttrCodeExtension = attributeMap
      .get("overrideObject")
      ?.extension?.find((e) => e?.url === ExtensionURLs.attrCode);

    if (attrCodeExtension) {
      attributeMap.set("attrCode", attrCodeExtension.valueString);
    } else if (overrideAttrCodeExtension) {
      attributeMap.set("attrCode", overrideAttrCodeExtension.valueString);
    }

    return attributeMap;
  };

  /**
   * Creates a generic display value string based on specified fields and their values in the attribute map.
   *
   * @param {Array} displayFields - An array of field names to include in the display value.
   * @param {Map} attributeMap - A Map containing attribute fields and their values.
   * @returns {string} - The constructed display value string.
   */
  const getGenericDisplayValue = (attribute, attributeMap) => {
    const displayFields = getDisplayFields(attribute);
    return displayFields
      .map((field) => {
        if (attributeMap.has(field)) {
          const value = attributeMap.get(field);
          return Array.isArray(value)
            ? `[${value
                .map((item) => item?.toString()?.toUpperCase())
                .join(", ")}]`
            : value?.toString()?.toUpperCase();
        }
        return getMissingFieldDisplay(field);
      })
      .join(", ");
  };

  /**
   * Creates an identifier display value string based on specified fields and their values in the attribute map
   *
   * @param {Array} displayFields - An array of field names to include in the display value.
   * @param {Map} attributeMap - A Map containing attribute fields and their values.
   * @returns {string} - The constructed display value string.
   */
  const getIdentifierDisplayValue = (attribute, attributeMap) => {
    const displayFields = getDisplayFields(attribute);
    return displayFields
      .map((field) => {
        if (attributeMap.has(field)) {
          return attributeMap.get(field);
        }
        return getMissingFieldDisplay(field);
      })
      .join(", ");
  };

  /**
   * Creates a display value string for the given attribute based on its type.
   *
   * @param {string} attribute - The attribute to create a display value for.
   * @param {Map} attributeMap - A Map containing attribute fields and their values.
   * @returns {string} - The constructed display value string.
   */
  const getItemDisplayValue = (attribute, attributeMap) => {
    switch (attribute) {
      case AttributeLabels.IDENTIFIER:
        return getIdentifierDisplayValue(attribute, attributeMap);
      default:
        return getGenericDisplayValue(attribute, attributeMap);
    }
  };

  /**
   * Creates a display value string based on specified fields and their values in the attribute map.
   *
   * @param {Array} displayFields - An array of field names to include in the display value.
   * @param {Map} attributeMap - A Map containing attribute fields and their values.
   * @returns {string} - The constructed display value string.
   */
  const getDisplayValue = (attribute, attributeMap) => {
    // if the attribute map is empty, return early with placeholder value
    const isEmpty = getIsEmptyAttribute(attribute, attributeMap);
    if (isEmpty) {
      return `[No ${capitalize(attribute)}]`;
    }

    const itemDisplayValue = getItemDisplayValue(attribute, attributeMap);

    // add "use" to the displayValue only if it is "OLD"
    const use = attributeMap?.has("use")
      ? attributeMap.get("use").toUpperCase()
      : "";
    if (!topLevelExtensionAttributes.includes(attribute) && use === "OLD") {
      return `[${use}] ${itemDisplayValue}`;
    }

    return itemDisplayValue;
  };

  /**
   * Extracts attribute values from a given object and populates them into the attribute map.
   *
   * @param {string} attribute - The attribute to extract values for.
   * @param {Object} object - The object from which to extract attribute values.
   * @param {Map} attributeMap - A Map to populate with extracted attribute values.
   * @returns {Map} - The updated attribute map with extracted values.
   */
  const extractItemValues = (attribute, object, attributeMap, fields) => {
    fields.forEach((field) => {
      if (object?.[field]) {
        attributeMap.set(field, object[field]);
      }
    });

    attributeMap.set("itemJSON", object);

    return attributeMap;
  };

  /**
   * Extracts identifier attribute values from a given object and populates them into the attribute map.
   *
   * @param {string} attribute - The attribute to extract values for.
   * @param {Object} object - The object from which to extract attribute values.
   * @param {Map} attributeMap - A Map to populate with extracted attribute values.
   * @returns {Map} - The updated attribute map with extracted values.
   */
  const extractIdentifierItemValues = (
    attribute,
    object,
    attributeMap,
    fields,
  ) => {
    const identifierExcludedSystems = [attributeMap.get("source"), "imdm"];
    const identifierExcludedCodes = ["COV", "MR"];
    const identifierOverriddenObject = object?.extension?.find(
      (e) => e?.url === ExtensionURLs.genericOverride,
    )?.valueIdentifier;

    fields.forEach((field) => {
      switch (field) {
        case "system":
          // Need to check in the override object for INVALID type identifier overrides as the system is not available
          // in the main object for these override.
          if (
            object?.system &&
            !identifierExcludedSystems.includes(object.system)
          ) {
            attributeMap.set("system", object.system);
          } else if (
            identifierOverriddenObject?.system &&
            !identifierExcludedSystems.includes(
              identifierOverriddenObject.system,
            )
          ) {
            attributeMap.set("system", identifierOverriddenObject?.system);
          }
          break;
        case "type":
          if (object?.type) {
            attributeMap.set("type", object.type);
          } else if (identifierOverriddenObject?.type) {
            attributeMap.set("type", identifierOverriddenObject.type);
          }
          if (object?.type?.coding) {
            object.type.coding.forEach((coding) => {
              if (
                coding?.code &&
                !identifierExcludedCodes.includes(coding.code)
              ) {
                attributeMap.set("code", coding.code);
              }
            });
          } else if (identifierOverriddenObject?.type?.coding) {
            identifierOverriddenObject.type.coding.forEach((coding) => {
              if (
                coding?.code &&
                !identifierExcludedCodes.includes(coding.code)
              ) {
                attributeMap.set("code", coding.code);
              }
            });
          }
          break;
        default:
          if (object?.[field]) {
            attributeMap.set(field, object?.[field]);
          }
          break;
      }
    });

    attributeMap.set("itemJSON", object);

    return attributeMap;
  };

  /**
   * Extracts attribute item values based on the attribute type.
   *
   * @param {string} attribute - The attribute to extract item values for.
   * @param {Object} object - The object from which to extract attribute values.
   * @param {Map} attributeMap - A Map to populate with extracted attribute values.
   * @returns {Map} - The updated attribute map with extracted item values.
   */
  const extractAttributeItemValues = (attribute, object, attributeMap) => {
    const fields = getAttributeFields(attribute); // Fields specific to the attribute that should be extracted

    switch (attribute) {
      case AttributeLabels.IDENTIFIER:
        return extractIdentifierItemValues(
          attribute,
          object,
          attributeMap,
          fields,
        );
      default:
        return extractItemValues(attribute, object, attributeMap, fields);
    }
  };

  /**
   * Returns a display string for a missing field.
   *
   * @param {string} field - The field name that is missing.
   * @returns {string} - A string indicating the field is missing.
   */
  const getMissingFieldDisplay = (field) => {
    return `[No ${capitalize(field)}]`;
  };

  /**
   * Sets telecom attribute item values for a given object and returns the populated attribute map.
   *
   * @param {string} attribute - The telecom attribute to set item values for.
   * @param {Object} object - The object from which to extract attribute values.
   * @param {Map} attributeMap - A Map to populate with attribute item values.
   * @returns {Map} - The populated attribute map with item values.
   */
  const setTelecomItemValues = (attribute, object, attributeMap) => {
    const objectSystem = object?.system?.toLowerCase();
    const overrideObjectSystem = object?.extension
      ?.find((e) => e?.url === ExtensionURLs.genericOverride)
      ?.valueContactPoint?.system?.toLowerCase();

    if (objectSystem === attribute || overrideObjectSystem === attribute) {
      setItemValues(attribute, object, attributeMap);
    }

    return attributeMap;
  };

  /**
   * Sets attribute item values for a given object and returns the populated attribute map.
   *
   * @param {string} attribute - The attribute to set item values for.
   * @param {Object} object - The object from which to extract attribute values.
   * @param {Map} attributeMap - A Map to populate with attribute item values.
   * @returns {Map} - The populated attribute map with item values.
   */
  const setItemValues = (attribute, object, attributeMap) => {
    extractAttributeOverrideValues(attribute, object, attributeMap);
    extractAttributeItemValues(attribute, object, attributeMap);
    attributeMap.set("displayValue", getDisplayValue(attribute, attributeMap));
    extractAttributeCode(object, attributeMap);

    return attributeMap;
  };

  /**
   * Sets attribute item values for a given object and returns the populated attribute map.
   *
   * @param {string} attribute - The attribute to set item values for.
   * @param {Object} object - The object from which to extract attribute values.
   * @param {Map} valueMap - A Map to populate with attribute item values.
   * @returns {Map} - The populated attribute map with item values.
   */
  const setAttributeItemValues = (attribute, object, initialAttributeMap) => {
    const attributeMap = new Map(initialAttributeMap);

    switch (attribute) {
      case AttributeLabels.PHONE:
      case AttributeLabels.EMAIL:
        return setTelecomItemValues(attribute, object, attributeMap);
      default:
        return setItemValues(attribute, object, attributeMap);
    }
  };

  /**
   * Sets attribute maps given a list of attribute objects.
   *
   * @param {string} attribute - The attribute to set maps for.
   * @param {Array} objects - An array of objects from which to extract attribute values.
   * @param {Map} valueMap - A Map to populate with attribute item values.
   * @returns {Array} - An array of populated attribute maps.
   */
  const setAttributeMaps = (attribute, initialAttributeMap, attributeItems) => {
    const attributeMaps = new Array();

    if (attributeItems) {
      attributeItems.forEach((obj) =>
        attributeMaps.push(
          setAttributeItemValues(attribute, obj, initialAttributeMap),
        ),
      );
    } else {
      attributeMaps.push(initialAttributeMap);
    }

    return attributeMaps;
  };

  /**
   * Extracts previous overrides from a given record. This acts as a template for override requests.
   *
   * @param {Object} record - The FHIR record from which to extract previous overrides.
   * @returns {Object} - An object containing previous override extensions.
   */
  const setPreviousOverrides = (record) => {
    // Add all DOB-related audit extensions
    const dobUrls = [
      Object.values(BirthDateOverrideURLs),
      Object.values(BirthDateRestoreURLs),
    ].flat();
    // Add all Gender-related audit extensions
    const genderUrls = [
      Object.values(GenderOverrideURLs),
      Object.values(GenderRestoreURLs),
    ].flat();

    const multipleBirthUrls = [
      Object.values(MultipleBirthOverrideURLs),
      Object.values(MultipleBirthRestoreURLs),
    ].flat();

    const previousOverrides = new Object();
    Object.keys(record).forEach((key) => {
      if (record[key] instanceof Array) {
        if (key === "extension") {
          record["extension"]
            .filter((e) => e.hasOwnProperty("url"))
            .forEach((e) => {
              if (dobUrls.includes(e["url"])) {
                previousOverrides["extension"] =
                  previousOverrides["extension"] || new Array();
                previousOverrides["extension"].push(e);
                if (e["url"] === ExtensionURLs.birthDateOverride) {
                  previousOverrides["birthDate"] = record["birthDate"];
                }
              }
              if (genderUrls.includes(e["url"])) {
                previousOverrides["extension"] =
                  previousOverrides["extension"] || new Array();
                previousOverrides["extension"].push(e);
                if (e["url"] === ExtensionURLs.genderOverride) {
                  previousOverrides["gender"] = record["gender"];
                }
              }
              if (multipleBirthUrls.includes(e["url"])) {
                previousOverrides["extension"] =
                  previousOverrides["extension"] || new Array();
                previousOverrides["extension"].push(e);
                if (e["url"] === ExtensionURLs.multipleBirthBooleanOverride) {
                  previousOverrides["multipleBirthBoolean"] =
                    record["multipleBirthBoolean"];
                }
              }
            });
        } else {
          record[key].forEach((item) => {
            const isOverrideOrRestore = item["extension"]?.find(
              (e) =>
                e.hasOwnProperty("url") &&
                (e["url"] === ExtensionURLs.genericOverride ||
                  Object.values(GenericRestoreURLs).includes(e["url"])),
            );

            if (isOverrideOrRestore) {
              previousOverrides[key] = previousOverrides[key] || [];
              previousOverrides[key].push(item);
            }
          });
        }
      }
    });
    return previousOverrides;
  };

  /**
   * Main logic to extract attribute values from records for use in the summary table and creating override/restore
   * requests.
   *
   * @param {string} attribute - The attribute to extract values for.
   * @param {Map} records - A Map of FHIR records to extract values from.
   * @returns {Array} - An array of Maps containing extracted attribute values.
   */
  const attributeMaps = new Array();
  if (!isEmpty(attribute)) {
    for (const [key, record] of records.entries()) {
      const initialAttributeMap = new Map()
        .set("id", record.id)
        .set("source", record.meta.source)
        .set("resourceType", record.resourceType)
        .set("meta", record.meta)
        .set("requestTemplate", setPreviousOverrides(record));
      const isTelecomType = telecomAttributes.includes(attribute);
      const isTopLevelExtensionType =
        topLevelExtensionAttributes.includes(attribute);
      // Determine the correct accessor for the record based on attribute
      let recordAccessor;
      switch (true) {
        case isTelecomType:
          recordAccessor = record[AttributeLabels.TELECOM];
          break;
        case isTopLevelExtensionType:
          recordAccessor = [record];
          break;
        default:
          recordAccessor = record[attribute];
          break;
      }

      attributeMaps.push(
        setAttributeMaps(attribute, initialAttributeMap, recordAccessor),
      );
    }
  }

  return attributeMaps;
};

export default AttributeValues;
